import { getLoginType, loginSuccess, pushRegistrationGAEvent, registrationSuccess } from '@comeon/analytics';
import Cookies from '@comeon/tomcat-cookies';
import { getUrlWithoutQuery } from '@comeon/utility';

import { getAppUrlScheme } from '~/components/app-bridge/app-properties';
import { REGISTRATION_TYPES } from '~/components/authentication/ducks/constants';
import { isChromeIOS, isFirefoxIOS, isIOSInApp, isIOSSafari, isIOS } from '~/utils/user-agent-utils';

import { BANKID_MODES, REGISTRATION_MODES } from './configs';

// cache order ref in a cookie for ios, because it opens new tab
const saveOrderRef = ref => {
	Cookies.set('orderRef', ref);
};

const clearOrderRefCookie = () => {
	Cookies.remove('orderRef');
};

// returns order ref from the url
const getUrlRef = () => {
	try {
		const urlParams = new URLSearchParams(window.location.search);
		const orderRef = urlParams.get('ref') ?? null;
		return orderRef;
	} catch (e) {
		return null;
	}
};

// retrieve either from cookie or url
const retrieveOrderRef = ref => {
	const orderRef = Cookies.get('orderRef', ref) || getUrlRef();
	return orderRef;
};

const getIOSRedirect = () => {
	// For iOS Chrome & iOS Mozilla, change the protocol to googlechromes:// or firefox:// to prevent redirection to Safari
	switch (true) {
		case isChromeIOS():
			return encodeURIComponent('googlechromes://');
		case isFirefoxIOS():
			return encodeURIComponent('firefox://');
		case isIOSSafari():
			return encodeURIComponent(window.location.href); // Safari doesn't have a specific URL scheme
		default: // Don't add redirect URL for iOS in-app browsers, since they open in Safari
			return 'null';
	}
};

const getBankIDAppURL = (autoStartToken, ref) => {
	const defaultRedirectURL = 'null'; // redirect null works for android
	const inAppBrowser = !window.appProperties && isIOSInApp(); // for in-app browsers on ios except our app
	const appUrlScheme = getAppUrlScheme(); // for our native apps
	const bankIDUrl = `bankid:///?autostarttoken=${autoStartToken}&redirect=`;

	switch (true) {
		case inAppBrowser:
			return `bankid://autostarttoken=${autoStartToken}&redirect=null`; // in-app has a different url with 2 // instead of 3
		case !!appUrlScheme:
			return `${bankIDUrl}${appUrlScheme}://`;
		case ref && isIOSSafari():
			saveOrderRef(ref); // save order ref in cookie only for ios safari
			return `${bankIDUrl}${getIOSRedirect()}`;
		case isIOS() && !appUrlScheme:
			return `${bankIDUrl}${getIOSRedirect()}`;
		default: // desktop & the rest, no changes needed, use default redirect
			return `${bankIDUrl}${defaultRedirectURL}`;
	}
};

// return location.search without order ref
const getUrlWithoutRef = () => {
	return getUrlWithoutQuery('ref');
};

const pushBankIDVerificationEvent = mode => {
	// need to log bankid-verification step event for registrations
	const isRegistrationMethod = REGISTRATION_MODES.includes(mode);
	if (isRegistrationMethod) {
		pushRegistrationGAEvent({
			step: 'bank-id-verification'
		});
	}
};

const setAnalyticsCookies = mode => {
	const data = {};
	switch (mode) {
		case BANKID_MODES.RETRIEVE_BALANCE:
			data.registrationMethod = 'retrieve-balance';
			break;
		case BANKID_MODES.SWISH:
			data.paymentMethod = 'swish';
			break;
		case BANKID_MODES.PLAY_NOW:
			data.registrationMethod = 'one-button-flow';
			data.paymentMethod = 'none';
			break;
		default:
			data.registrationMethod = 'full-account';
			break;
	}
	if (data.registrationMethod) {
		Cookies.set('registration-method', data.registrationMethod);
	}
	if (data.paymentMethod) {
		Cookies.set('payment-method', data.paymentMethod);
	}
};

// TODO: Can we have one type of event, which similar data structure.
const postBankIDAnalytics = ({ isRegistration, encryptedPID, qrMode }) => {
	const loginType = getLoginType();

	const method = `${qrMode ? 'qr-code' : 'BankID'}${loginType ? ` - ${loginType}` : ''}`;

	if (isRegistration) {
		setTimeout(() => {
			registrationSuccess({
				method,
				encryptedPID
			});
		}, 500); // add a delay to make sure session is found
	} else {
		loginSuccess({
			method,
			oldEvent: true
		});
	}
};

const getBankIDRequestType = mode => {
	switch (mode) {
		case BANKID_MODES.PLAY_NOW:
			return REGISTRATION_TYPES.BANKID_ONE_BUTTON;
		case BANKID_MODES.RETRIEVE_BALANCE:
			return REGISTRATION_TYPES.BANKID_RETRIEVE_BALANCE;
		case BANKID_MODES.SWISH:
			return REGISTRATION_TYPES.BANKID_SWISH;
		default:
			return mode;
	}
};

const showAccountLink = error => error === 'BANKID_BL_PLAYER_NOT_FOUND';

// check if bankid session criteria is fulfilled
const isSessionCriteriaFulfilled = sessionCriteria => {
	const {
		shouldShowComplianceMessage,
		isEmailSet,
		shouldShowDepositLimitPage,
		isMobileNumberSet,
		shouldShowPrivacyPolicyMessage
	} = sessionCriteria;
	const criteria = {
		shouldShowComplianceMessage,
		shouldShowPrivacyPolicyMessage,
		shouldShowEmail: !isEmailSet,
		shouldShowDepositLimitPage,
		...(sessionCriteria.hasOwnProperty('isMobileNumberSet') // eslint-disable-line
			? { shouldShowPhone: !isMobileNumberSet }
			: {})
	};
	// fulfilled if everything is done and nothing needs to be shown
	return Object.keys(criteria).every(c => !criteria[c]);
};

export {
	clearOrderRefCookie,
	getBankIDAppURL,
	getBankIDRequestType,
	getUrlRef,
	getUrlWithoutRef,
	isSessionCriteriaFulfilled,
	postBankIDAnalytics,
	pushBankIDVerificationEvent,
	retrieveOrderRef,
	setAnalyticsCookies,
	showAccountLink
};
